import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Summarize as SummarizeIcon,
  Error as UrgentIcon,
  Psychology as ToneIcon
} from '@mui/icons-material';
import { aiService } from '../services/api';

const MessageAIFeatures = ({ messages, selectedMessage, onSuggestedReplyClick }) => {
  const [showSummary, setShowSummary] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [showToneAnalysis, setShowToneAnalysis] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const [toneAnalysis, setToneAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const handleSummarize = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await aiService.summarizeConversation(messages);
      // Backend returns { success: true, summary: { ... } }
      setSummary(response.data.summary);
      setShowSummary(true);
    } catch (err) {
      console.error('Error summarizing:', err);
      setError('Erreur lors de la génération du résumé');
    } finally {
      setLoading(false);
    }
  };

  const handleGetReplies = async () => {
    if (!selectedMessage) return;

    setLoading(true);
    setError(null);
    try {
      const response = await aiService.suggestReplies({
        messageContent: selectedMessage.content
      });
      // Backend returns { success: true, suggestions: { suggestions: [...] } }
      setSuggestedReplies(response.data.suggestions?.suggestions || []);
      setShowReplies(true);
    } catch (err) {
      console.error('Error getting replies:', err);
      setError('Erreur lors de la génération des réponses');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeTone = async () => {
    if (!selectedMessage) return;

    setLoading(true);
    setError(null);
    try {
      const response = await aiService.analyzeTone(selectedMessage.content);
      setToneAnalysis(response.data);
      setShowToneAnalysis(true);
    } catch (err) {
      console.error('Error analyzing tone:', err);
      setError('Erreur lors de l\'analyse du ton');
    } finally {
      setLoading(false);
    }
  };

  const getToneColor = (tone) => {
    const toneColors = {
      'frustré': 'error',
      'urgent': 'warning',
      'positif': 'success',
      'professionnel': 'primary',
      'amical': 'info',
      'neutre': 'default'
    };
    return toneColors[tone] || 'default';
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === 'positif') return '😊';
    if (sentiment === 'négatif') return '😟';
    return '😐';
  };

  return (
    <Box>
      {/* AI Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Tooltip title="Résumer la conversation">
          <IconButton
            size="small"
            onClick={handleSummarize}
            disabled={loading || messages.length === 0}
            color="primary"
          >
            {loading ? <CircularProgress size={20} /> : <SummarizeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {selectedMessage && (
          <>
            <Tooltip title="Suggérer des réponses">
              <IconButton
                size="small"
                onClick={handleGetReplies}
                disabled={loading}
                color="secondary"
              >
                <AIIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Analyser le ton">
              <IconButton
                size="small"
                onClick={handleAnalyzeTone}
                disabled={loading}
                color="info"
              >
                <ToneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Dialog */}
      <Dialog open={showSummary} onClose={() => setShowSummary(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SummarizeIcon color="primary" />
          Résumé de la conversation
        </DialogTitle>
        <DialogContent>
          {summary && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                {summary.summary}
              </Typography>

              {summary.key_points && summary.key_points.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Points clés :
                  </Typography>
                  <List dense>
                    {summary.key_points.map((point, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={`• ${point}`} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {summary.decisions && summary.decisions.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Décisions prises :
                  </Typography>
                  <List dense>
                    {summary.decisions.map((decision, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={`✓ ${decision}`} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {summary.action_items && summary.action_items.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                    Actions à entreprendre :
                  </Typography>
                  <List dense>
                    {summary.action_items.map((action, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={`→ ${action}`} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSummary(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Suggested Replies Dialog */}
      <Dialog open={showReplies} onClose={() => setShowReplies(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="secondary" />
          Réponses suggérées
        </DialogTitle>
        <DialogContent>
          {suggestedReplies.length > 0 ? (
            <List>
              {suggestedReplies.map((reply, idx) => (
                <ListItem
                  key={idx}
                  button
                  onClick={() => {
                    onSuggestedReplyClick && onSuggestedReplyClick(reply);
                    setShowReplies(false);
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemText
                    primary={reply}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucune suggestion disponible
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReplies(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Tone Analysis Dialog */}
      <Dialog open={showToneAnalysis} onClose={() => setShowToneAnalysis(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToneIcon color="info" />
          Analyse du ton
        </DialogTitle>
        <DialogContent>
          {toneAnalysis && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={toneAnalysis.tone}
                  color={getToneColor(toneAnalysis.tone)}
                  sx={{ fontWeight: 'bold' }}
                />
                <Typography variant="h4">{getSentimentIcon(toneAnalysis.sentiment)}</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {toneAnalysis.analysis}
              </Typography>

              {toneAnalysis.emotions && toneAnalysis.emotions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Émotions détectées :
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {toneAnalysis.emotions.map((emotion, idx) => (
                      <Chip key={idx} label={emotion} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {toneAnalysis.requires_careful_response && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ Ce message nécessite une réponse prudente
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Conseils de réponse :
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {toneAnalysis.response_suggestions}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowToneAnalysis(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageAIFeatures;
